import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useWorkspace } from "@/lib/workspace";
import { LayoutGrid, Users, Calendar, Stethoscope, Receipt, Brain, Settings as SettingsIcon, LogOut, Search, CreditCard, Shield, Package, Layers, User as UserIcon, X, MessageCircle, Trophy, UserCheck, PanelLeftClose, PanelLeft, Building2, ShoppingCart, BarChart3, Database, Sparkles, Megaphone, ShieldCheck, FlaskConical, Boxes, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSubscription } from "@/lib/subscription";
import { TrialCountdown } from "@/pages/Subscription";
import SEO from "@/components/SEO";

// Sectioned navigation — Patient Care · Operations · Insights · Administration.
// Every item declares which workspace module gates it (or `always: true` for
// core items that render regardless).
//
// `feature`  → clinic-plan feature flag (existing gate — unchanged)
// `module`   → workspace module toggle (new gate)
// `roles`    → visible only to these roles
// `admin`    → visible only when role === "admin"
// `super`    → visible only when user.is_platform_admin
const ICONS = {
  LayoutGrid, Users, Calendar, Stethoscope, Receipt, Package, MessageCircle,
  UserCheck, BarChart3, Database, Layers, Sparkles, Megaphone, ShieldCheck,
  FlaskConical, Boxes, Building2, FileText, Settings: SettingsIcon,
};

const SECTIONS = [
  {
    id: "patient_care", label: "Patient Care",
    items: [
      { to: "/app",             end: true, module: "dashboard",     label: "Dashboard",     icon: LayoutGrid, testid: "nav-dashboard" },
      { to: "/app/patients",               module: "patients",      label: "Patients",      icon: Users,      testid: "nav-patients" },
      { to: "/app/calendar",               module: "calendar",      label: "Calendar",      icon: Calendar,   testid: "nav-calendar" },
      { to: "/app/reception/queue",        module: "waiting_room",  label: "Waiting Room",  icon: UserCheck,  testid: "nav-reception-queue", roles: ["admin", "receptionist"] },
      { to: "/app/treatments",             module: "treatments",    label: "Treatments",    icon: Stethoscope,testid: "nav-treatments" },
    ],
  },
  {
    id: "operations", label: "Operations",
    items: [
      { to: "/app/billing",   module: "billing",   label: "Billing",   icon: Receipt,        testid: "nav-billing" },
      { to: "/app/inventory", module: "inventory", label: "Inventory", icon: Package,        testid: "nav-inventory", feature: "inventory" },
      { to: "/app/whatsapp",  module: "whatsapp",  label: "WhatsApp",  icon: MessageCircle,  testid: "nav-whatsapp", feature: "whatsapp" },
    ],
  },
  {
    id: "insights", label: "Insights",
    items: [
      { to: "/app/analytics", module: "analytics", label: "Analytics", icon: BarChart3,      testid: "nav-analytics" },
    ],
  },
  {
    id: "administration", label: "Administration",
    items: [
      // Data Migration — visible only when the module is enabled, and only
      // for admins (per spec: "Only Admin can see it").
      { to: "/app/data-migration", module: "data_migration", label: "Data Migration", icon: Database, testid: "nav-data-migration", admin: true },
      { to: "/app/settings",       module: "settings",       label: "Settings",       icon: SettingsIcon, testid: "nav-settings" },
    ],
  },
];

// Persist collapsed preference across sessions.
const SIDEBAR_PREF_KEY = "viyora.sidebar.collapsed";

// Auto-collapse default when no user preference is set. Content-first design:
// - ≥1600px  → expanded
// - <1600px  → collapsed (user can still expand manually)
function getInitialCollapsed() {
  try {
    const raw = localStorage.getItem(SIDEBAR_PREF_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch { /* SSR / disabled localStorage */ }
  if (typeof window !== "undefined" && window.innerWidth < 1600) return true;
  return false;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { sub } = useSubscription();
  const workspace = useWorkspace();
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);

  const handleLogout = () => { logout(); nav("/login"); };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_PREF_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Auto-force collapsed under 1280px, but leave the user's saved preference
  // intact so re-expanding on a larger screen still works.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 1280 && !collapsed) setCollapsed(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [collapsed]);

  // Hard-gate the app when trial has ended and no paid plan.
  const trialEnded = sub && !sub.active;
  if (trialEnded && !location.pathname.startsWith("/trial-ended")) {
    return <Navigate to="/trial-ended" replace />;
  }

  // Force new clinics through the 6-step onboarding wizard before they see the app.
  if (sub && sub.onboarding_complete === false && !location.pathname.startsWith("/onboarding")) {
    return <Navigate to="/onboarding" replace />;
  }

  const asideWidth = collapsed ? "w-[72px]" : "w-[224px]";

  // Filter every section's items against role + workspace module toggle.
  const isVisible = (item) => {
    if (item.module && !workspace.isEnabled(item.module)) return false;
    if (item.roles && user && !item.roles.includes(user.role)) return false;
    if (item.admin && user?.role !== "admin") return false;
    if (item.super && !user?.is_platform_admin) return false;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink flex" data-sidebar-collapsed={collapsed ? "1" : "0"}>
      <SEO title="App" noindex={true} />
      {/* -------------------- Sidebar (sectioned, module-aware) -------------------- */}
      <aside
        className={`hidden lg:flex ${asideWidth} shrink-0 bg-white border-r border-[#E8EAE9] flex-col sticky top-0 h-screen z-30 transition-[width] duration-200 ease-out`}
        data-testid="app-sidebar"
      >
        {/* Header: logo + clinic name (or icon-only when collapsed) */}
        <div className={`flex items-center gap-2 border-b border-[#E8EAE9] ${collapsed ? "px-3 py-3 justify-center" : "px-3.5 py-3"}`}>
          <ClinicLogo clinic={user?.clinic} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm leading-tight truncate" data-testid="sidebar-clinic-name">
                {user?.clinic?.name || "Viyora"}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-ink-soft mt-0.5">
                Powered by Viyora
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={toggleCollapsed}
              data-testid="sidebar-collapse-toggle"
              title="Collapse sidebar"
              className="text-ink-soft hover:text-ink w-7 h-7 rounded-md hover:bg-linen grid place-items-center"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sectioned nav — headers hide when collapsed */}
        <nav className={`flex-1 overflow-y-auto min-h-0 ${collapsed ? "px-2 py-2" : "px-2 py-2"}`}>
          {SECTIONS.map((section) => {
            const visibleItems = section.items.filter(isVisible);
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.id} className="mb-3 last:mb-0" data-testid={`nav-section-${section.id}`}>
                {!collapsed && (
                  <div className="px-2.5 pt-2 pb-1 text-[9px] uppercase tracking-[0.18em] text-ink-soft/70 font-medium">
                    {section.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {visibleItems.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={n.testid}
                      title={collapsed ? n.label : undefined}
                      className={({ isActive }) =>
                        `group flex items-center rounded-lg text-sm transition-all ${
                          collapsed ? "justify-center h-10 w-full" : "gap-3 px-2.5 h-10"
                        } ${
                          isActive
                            ? "bg-pine/10 text-ink font-medium [&>svg]:text-pine"
                            : "text-ink-mid [&>svg]:text-ink-soft hover:bg-linen/60 hover:text-ink"
                        }`
                      }
                    >
                      <n.icon className="w-[18px] h-[18px] shrink-0" />
                      {!collapsed && <span className="truncate">{n.label}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Super-admin only — Platform Admin never appears for clinic admins. */}
          {user?.is_platform_admin && (
            <NavLink
              to="/platform"
              data-testid="nav-platform"
              title={collapsed ? "Platform admin" : undefined}
              className={({ isActive }) =>
                `flex items-center rounded-lg text-sm transition-all mt-2 border-t border-[#E8EAE9] pt-3 ${
                  collapsed ? "justify-center h-10" : "gap-3 px-2.5 h-10"
                } ${
                  isActive
                    ? "bg-pine/10 text-ink font-medium [&>svg]:text-pine"
                    : "text-ink-mid [&>svg]:text-ink-soft hover:bg-linen/60"
                }`
              }
            >
              <Shield className="w-[18px] h-[18px] shrink-0" />
              {!collapsed && <span className="truncate">Platform admin</span>}
            </NavLink>
          )}
        </nav>

        {/* Compressed profile card — expand toggle when collapsed */}
        <div className={`border-t border-[#E8EAE9] ${collapsed ? "p-2" : "p-2.5"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={toggleCollapsed}
                data-testid="sidebar-expand-toggle"
                title="Expand sidebar"
                className="w-9 h-9 rounded-md text-ink-soft hover:text-ink hover:bg-linen grid place-items-center"
              >
                <PanelLeft className="w-4 h-4" />
              </button>
              <div
                className="w-9 h-9 rounded-full bg-sand grid place-items-center text-linen font-display font-bold text-sm"
                title={`${user?.name || ""} · ${user?.role || ""}`}
              >
                {user?.name?.[0] || "U"}
              </div>
              <button
                data-testid="btn-logout"
                onClick={handleLogout}
                title="Sign out"
                className="w-9 h-9 rounded-md text-ink-soft hover:text-ink hover:bg-linen grid place-items-center"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5 px-1 py-1">
                <div className="w-8 h-8 rounded-full bg-sand grid place-items-center text-linen font-display font-bold text-xs">
                  {user?.name?.[0] || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{user?.name}</div>
                  <div className="text-[9px] uppercase tracking-widest text-ink-soft">{user?.role}</div>
                </div>
                <button
                  data-testid="btn-logout"
                  onClick={handleLogout}
                  title="Sign out"
                  className="w-7 h-7 rounded-md text-ink-soft hover:text-ink hover:bg-linen grid place-items-center"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Subtle Viyora attribution — tight, non-intrusive */}
        <a
          href="https://viyora.co.in"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-viyora-badge"
          title="Powered by Viyora"
          className={`flex items-center border-t border-[#E8EAE9] text-[9px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink transition-colors ${
            collapsed ? "justify-center py-1.5" : "justify-center gap-1.5 px-3 py-1.5"
          }`}
        >
          <img src="/viyora-icon-64.png" alt="" className="w-3 h-3 rounded-sm opacity-60" />
          {!collapsed && <span>Powered by Viyora</span>}
        </a>
      </aside>

      {/* -------------------- Main content — gains the reclaimed width -------------------- */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-[#E8EAE9] bg-white/70 backdrop-blur px-5 lg:px-6 flex items-center justify-between">
          <GlobalSearch />
          <div className="flex items-center gap-3">
            {sub?.trial?.is_trial && (
              <button
                onClick={() => nav("/app/subscription")}
                data-testid="trial-countdown-nav"
                className="cursor-pointer hover:opacity-80"
                title="View plan & upgrade"
              >
                <TrialCountdown trial={sub.trial} compact />
              </button>
            )}
            <span className="overline text-ink-mid hidden sm:inline">{user?.role}</span>
            <div className="w-8 h-8 rounded-full bg-pine text-linen grid place-items-center text-sm font-medium">{user?.name?.[0]}</div>
          </div>
        </header>
        <div className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </div>
      </main>

      {/* -------------------- Floating Viyora Copilot (global) -------------------- */}
      <FloatingCopilot />
    </div>
  );
}

/**
 * FloatingCopilot — bottom-right button + Ctrl/Cmd+K shortcut.
 * Both routes to the existing `/app/ai` full-page Copilot. Kept intentionally
 * simple: no in-place overlay because AICommand is a stateful, multi-session
 * page whose lifecycle we don't want to duplicate.
 */
function FloatingCopilot() {
  const nav = useNavigate();
  const location = useLocation();
  const workspace = useWorkspace();
  const aiEnabled = workspace.isEnabled("ai_features");
  useEffect(() => {
    const onKey = (e) => {
      // Ctrl/Cmd + K → open Copilot. Ignore when the shortcut originates from
      // an input/textarea/contenteditable so it doesn't hijack user typing.
      // Also ignored when the AI module is off — nothing should happen.
      const t = e.target;
      const inEditable =
        t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !inEditable && aiEnabled) {
        e.preventDefault();
        nav("/app/ai");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, aiEnabled]);

  // Hide when the user is already on the Copilot page OR the AI Features
  // module is disabled — a true feature flag never leaves an entry-point.
  if (location.pathname.startsWith("/app/ai")) return null;
  if (!aiEnabled) return null;

  return (
    <button
      type="button"
      onClick={() => nav("/app/ai")}
      data-testid="floating-copilot"
      title="Ask Viyora Copilot  (Ctrl/⌘ + K)"
      className="fixed bottom-6 right-6 z-40 group inline-flex items-center gap-2 rounded-full bg-pine text-linen pl-3.5 pr-4 h-11 shadow-lg shadow-pine/30 hover:shadow-xl hover:shadow-pine/40 hover:scale-[1.03] active:scale-[0.98] transition-all"
    >
      <Brain className="w-4 h-4" />
      <span className="text-sm font-medium">Ask AI</span>
      <span className="ml-1 hidden sm:inline text-[10px] uppercase tracking-widest opacity-70 border border-linen/30 rounded px-1.5 py-0.5">Ctrl K</span>
    </button>
  );
}


// ------------------ Global top-bar search ------------------
// Live, debounced patient lookup — searches name, phone, email.
// Keyboard: ↑/↓ to move through results, Enter to open, Esc to close.
function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const query = q.trim().toLowerCase();
    if (!query) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients");
        const filtered = (data || [])
          .filter((p) =>
            (p.name || "").toLowerCase().includes(query) ||
            (p.phone || "").toLowerCase().includes(query) ||
            (p.email || "").toLowerCase().includes(query)
          )
          .slice(0, 8);
        setResults(filtered);
        setActive(0);
      } catch { /* silent */ } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goTo = (p) => {
    setOpen(false); setQ("");
    nav(`/app/patients/${p.id}`);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); goTo(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showResults = open && q.trim().length > 0;

  return (
    <div ref={wrapRef} className="relative w-96 max-w-[60vw]">
      <div className="flex items-center gap-3 text-ink-mid border border-transparent hover:border-[#E8EAE9] focus-within:border-pine/40 rounded-full px-3 py-1.5 transition">
        <Search className="w-4 h-4 flex-shrink-0" />
        <input
          data-testid="global-search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search patients by name, phone, or email…"
          className="bg-transparent text-sm w-full focus:outline-none placeholder:text-ink-soft"
        />
        {q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setResults([]); }}
                  className="text-ink-soft hover:text-ink flex-shrink-0" type="button" aria-label="Clear">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showResults && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white border border-[#E8EAE9] rounded-2xl shadow-lg overflow-hidden z-50"
             data-testid="global-search-results">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-ink-soft">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-ink-soft">
              No patients match &ldquo;{q}&rdquo;.
            </div>
          )}
          {!loading && results.map((p, i) => (
            <button
              key={p.id}
              type="button"
              data-testid={`global-search-result-${p.id}`}
              onClick={() => goTo(p)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#F0EBE0] last:border-b-0 transition
                ${i === active ? "bg-linen" : "hover:bg-linen/60"}`}
            >
              <div className="w-8 h-8 rounded-full bg-pine/10 text-ink grid place-items-center flex-shrink-0">
                <UserIcon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink truncate">{p.name}</div>
                <div className="text-xs text-ink-mid truncate">
                  {p.phone}{p.email ? ` · ${p.email}` : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ClinicLogo — shows the uploaded clinic logo, falling back to the Viyora mark.
 * The `logo_url` stored on the clinic is a relative path (`/api/public/...`)
 * so we prefix with REACT_APP_BACKEND_URL for absolute-safe rendering.
 */
function ClinicLogo({ clinic, size = "md" }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const raw = clinic?.logo_url;
  const src = raw ? (raw.startsWith("http") ? raw : `${backend}${raw}`) : "/viyora-icon-64.png";
  const cls = size === "sm" ? "w-8 h-8" : "w-9 h-9";
  return (
    <img
      src={src}
      alt={clinic?.name || "Viyora"}
      data-testid="sidebar-clinic-logo"
      className={`${cls} rounded-lg object-contain bg-white border border-[#E8EAE9]`}
      onError={(e) => { e.currentTarget.src = "/viyora-icon-64.png"; }}
    />
  );
}

